import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Users, Pencil, Eye } from 'lucide-react';
import { vehicleOwnerService } from '../../services/vehicleOwnerService';
import vehicleService from '../../services/vehicleService';
import { useAuth } from '../../context/AuthContext';
import Alert from '../common/Alert';
import {
  PageLayout,
  Button,
  DetailCard,
  DetailRow,
  DataTable,
  StatusBadge,
} from '../common/index.js';

export default function ViewVehicleOwner() {
  const { ownerId } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();

  const [owner, setOwner] = useState(null);
  const [vehicles, setVehicles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [vehiclesLoading, setVehiclesLoading] = useState(false);
  const [activeTab, setActiveTab] = useState('info');

  const canManage =
    user?.role === 'System Admin' || user?.role === 'Admin' || user?.role === 'Accountant';

  useEffect(() => {
    fetchOwner();
  }, [ownerId]);

  const fetchOwner = async () => {
    try {
      setLoading(true);
      const response = await vehicleOwnerService.getOwner(ownerId);
      const data = response.data?.data || response.data;
      setOwner(data);

      if (data?.vehicles && data.vehicles.length > 0) {
        setVehiclesLoading(true);
        try {
          const vehicleDetails = await Promise.all(
            data.vehicles.map((vehicleId) =>
              vehicleService
                .getVehicle(vehicleId)
                .then((res) => res.data?.data || res.data)
                .catch(() => ({ vehicle_id: vehicleId, vehicle_number: vehicleId, _error: true }))
            )
          );
          setVehicles(vehicleDetails);
        } catch (err) {
          console.error('Error fetching vehicle details:', err);
        } finally {
          setVehiclesLoading(false);
        }
      }
    } catch (error) {
      console.error('Error fetching owner:', error);
      Alert.error('Failed to load vehicle owner details');
      navigate('/vehicle-owners');
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '—';
    return new Date(dateStr).toLocaleDateString('en-IN', {
      day: '2-digit',
      month: 'short',
      year: 'numeric',
    });
  };

  const vehicleColumns = [
    {
      key: 'vehicle_number',
      label: 'Vehicle Number',
      render: (vehicle) =>
        vehicle._error ? (
          vehicle.vehicle_number || vehicle.vehicle_id
        ) : (
          <button
            onClick={() => navigate(`/vehicles/view/${vehicle.vehicle_id}`)}
            className="font-medium text-brand hover:underline text-left"
          >
            {vehicle.vehicle_number || vehicle.vehicle_id}
          </button>
        ),
    },
    {
      key: 'vehicle_name',
      label: 'Name / Manufacturer',
      render: (vehicle) => vehicle.vehicle_name || '—',
    },
    {
      key: 'vehicle_type',
      label: 'Type',
      render: (vehicle) =>
        vehicle.vehicle_type ? vehicle.vehicle_type.replace(/_/g, ' ') : '—',
    },
    {
      key: 'status',
      label: 'Status',
      render: (vehicle) =>
        vehicle.status ? (
          <StatusBadge status={vehicle.status} />
        ) : vehicle._error ? (
          <span className="text-xs text-red-400">Error</span>
        ) : (
          '—'
        ),
    },
  ];

  const getVehicleActions = (vehicle) =>
    vehicle._error
      ? []
      : [
          {
            label: 'View Vehicle',
            icon: Eye,
            onClick: () => navigate(`/vehicles/view/${vehicle.vehicle_id}`),
          },
        ];

  if (loading) {
    return (
      <PageLayout title="Vehicle Owner Details" icon={Users}>
        <div className="flex items-center justify-center py-16">
          <div className="text-center">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-brand mx-auto mb-4" />
            <p className="text-gray-600">Loading vehicle owner details...</p>
          </div>
        </div>
      </PageLayout>
    );
  }

  if (!owner) {
    return (
      <PageLayout title="Vehicle Owner Details" icon={Users}>
        <DetailCard>
          <p className="text-center text-gray-600">Vehicle owner not found</p>
          <div className="mt-4 flex justify-center">
            <Button variant="outline" onClick={() => navigate('/vehicle-owners')}>
              Back to Vehicle Owners
            </Button>
          </div>
        </DetailCard>
      </PageLayout>
    );
  }

  const tabClass = (tab) =>
    `pb-3 px-1 border-b-2 font-medium text-sm transition-colors ${
      activeTab === tab
        ? 'border-brand text-secondary'
        : 'border-transparent text-gray-500 hover:text-gray-700'
    }`;

  return (
    <PageLayout
      title="Vehicle Owner Details"
      icon={Users}
      actions={
        <>
          <Button variant="outline" onClick={() => navigate('/vehicle-owners')}>
            Back to Owners
          </Button>
          {canManage && (
            <Button icon={Pencil} onClick={() => navigate(`/vehicle-owners/edit/${ownerId}`)}>
              Edit Owner
            </Button>
          )}
        </>
      }
    >
      <DetailCard className="mb-4">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 mb-6 pb-4 border-b border-gray-100">
          <div>
            <p className="text-xs text-gray-500 font-medium uppercase tracking-wider mb-1">Owner ID</p>
            <p className="font-mono text-base font-bold text-secondary">{owner.vehicle_owner_id}</p>
          </div>
          <div className="text-left sm:text-right">
            <p className="text-xs text-gray-500 font-medium uppercase tracking-wider mb-1">
              Vehicles Assigned
            </p>
            <span className="inline-flex px-3 py-1 bg-brand-light text-brand text-sm font-semibold rounded-full">
              {owner.vehicles?.length || 0} vehicle{owner.vehicles?.length !== 1 ? 's' : ''}
            </span>
          </div>
        </div>

        <div className="border-b border-gray-200 mb-6">
          <nav className="flex space-x-8">
            <button type="button" onClick={() => setActiveTab('info')} className={tabClass('info')}>
              Owner Information
            </button>
            <button type="button" onClick={() => setActiveTab('bank')} className={tabClass('bank')}>
              Bank Details
            </button>
            <button type="button" onClick={() => setActiveTab('vehicles')} className={tabClass('vehicles')}>
              Assigned Vehicles
              {owner.vehicles?.length > 0 && (
                <span className="ml-2 px-1.5 py-0.5 text-xs bg-blue-100 text-brand rounded-full">
                  {owner.vehicles.length}
                </span>
              )}
            </button>
          </nav>
        </div>

        {activeTab === 'info' && (
          <>
            <DetailRow label="Owner Name" value={owner.owner_name || '—'} />
            <DetailRow label="Email Address" value={owner.owner_email || '—'} />
            <DetailRow label="Contact Number" value={owner.owner_contact || '—'} />
            <DetailRow
              label="Commission %"
              value={
                owner.vehicle_owner_percentage != null
                  ? `${owner.vehicle_owner_percentage}%`
                  : '0%'
              }
            />
            <DetailRow label="Created At" value={formatDate(owner.created_at)} />
            <DetailRow label="Last Updated" value={formatDate(owner.updated_at)} />
          </>
        )}

        {activeTab === 'bank' && (
          <>
            <DetailRow
              label="Bank Account Number"
              value={owner.bank_account || 'Not provided'}
            />
            <DetailRow label="IFSC Code" value={owner.ifsc_code || 'Not provided'} />
          </>
        )}

        {activeTab === 'vehicles' && (
          <DataTable
            columns={vehicleColumns}
            data={vehicles}
            loading={vehiclesLoading}
            emptyMessage="No vehicles assigned to this owner"
            rowKey="vehicle_id"
            actions={getVehicleActions}
          />
        )}
      </DetailCard>
    </PageLayout>
  );
}
